/* eslint-disable @typescript-eslint/naming-convention */
import { dateFormat } from '@astronautsid/wpe-utils'
import { AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import {
  PostReplenishmentScheduleRequestType,
  PostReplenishmentScheduleResponseType,
  PutReplenishmentScheduleResponseType,
  CreateUpdateReplenishmentSchedulePayloadType,
  deleteReplenishmentSchedule,
  getReplenishmentSchedule,
  getReplenishmentScheduleDetail,
  getWarehouseLocationsAPI,
  postReplenishmentScheduleAPI,
  putReplenishmentScheduleAPI,
  putReplenishmentScheduleWims,
  postReplenishmentScheduleWims,
  getNewCategoriesAPI,
} from 'features/SupplyOrder/services/replenishment'
import { fetchFeatureFlagByLocation } from 'utils/apiList/flagManagement'
import { callErrorMsg } from 'helpers/errorMsg'
import { serializeQueryToURL } from 'utils/queryParamsURL'
import { toastSuccess } from 'utils/toast'
import { AppDispatchType, RootStateType } from 'store'
import {
  SCHEDULE_TYPE_DATA,
  ReplenishmentScheduleFilterType,
  FormScheduleDetailType,
  setIsLoading,
  setIsLoadingConstant,
  setReplenishmentScheduleListData,
  setReplenishmentScheduleFilter,
  setConstants,
  setAddEditScheduleFormPopup,
  setPopupAddEditFormOnFetchDetailSucceed,
  resetPopupAddEditSchedule,
} from './slice'

type ActGetFeatureFlagByLocationIdParamsType = {
  locationId: number
  callbackFunction?: () => void
}

export const actGetFeatureFlagByLocationId =
  ({ locationId, callbackFunction }: ActGetFeatureFlagByLocationIdParamsType) =>
  async (dispatch: AppDispatchType) => {
    try {
      const { data } = await fetchFeatureFlagByLocation({
        location_id: locationId,
        feature_flag_device: 1,
      })
      const isFlagActive =
        data.data.hub_feature_flags.find((item) => item.key === 'replenishmentv3')?.active || false

      const scheduleTypeOptions = [
        SCHEDULE_TYPE_DATA.once,
        SCHEDULE_TYPE_DATA.recurring,
        ...(isFlagActive ? [SCHEDULE_TYPE_DATA.everywhere] : []),
      ]
      dispatch(
        setConstants({
          scheduleTypeOptions,
        }),
      )
      dispatch(setAddEditScheduleFormPopup({ key: 'isFlagReplenishV3Active', value: isFlagActive }))

      if (callbackFunction) callbackFunction()
    } catch (err) {
      callErrorMsg(err)
    }
  }

export const actGetReplenishmentSchedule =
  (params?: ReplenishmentScheduleFilterType) => async (dispatch: AppDispatchType) => {
    dispatch(setIsLoading(true))
    try {
      const { page_size, page_index, ...rest } = params || {}
      const paramsToSubmit = {
        'pagination.page_size': page_size || 20,
        'pagination.page_index': page_index || 1,
        ...rest,
      }
      const { data } = await getReplenishmentSchedule(paramsToSubmit)
      dispatch(setReplenishmentScheduleListData(data))
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const fetchGetReplenishmentScheduleConstants = () => async (dispatch: AppDispatchType) => {
  try {
    dispatch(setIsLoadingConstant(true))
    const locations = await getWarehouseLocationsAPI()
    const categories = await getNewCategoriesAPI({
      'pagination.page_index': 1,
      'pagination.page_size': 100,
    })

    const categoriesMapped = categories.data.data.map((cat) => ({
      category_id: cat.id,
      category_name: cat.name,
    }))

    const scheduleTypeOptions = [
      {
        name: 'Satu Kali',
        value: 'once',
      },
      {
        name: 'Berulang',
        value: 'recurring',
      },
    ]

    const scheduleOptions = [
      {
        name: 'Saat Ini',
        value: 'now',
      },
      {
        name: 'Pilih Jadwal',
        value: 'selectSchedule',
      },
    ]

    const dayOptions = [
      {
        name: 'Senin',
        value: 'Monday',
      },
      {
        name: 'Selasa',
        value: 'Tuesday',
      },
      {
        name: 'Rabu',
        value: 'Wednesday',
      },
      {
        name: 'Kamis',
        value: 'Thursday',
      },
      {
        name: 'Jumat',
        value: 'Friday',
      },
      {
        name: 'Sabtu',
        value: 'Saturday',
      },
      {
        name: 'Minggu',
        value: 'Sunday',
      },
    ]

    const skuMaxOptions = ['5', '10', '20', '50', '100']

    const timeOptions = Array.from({ length: 24 }, (__, i) => i).reduce(
      (time, hour, index) => {
        time.push({
          no: index + 1,
          name: dayjs().set('hour', hour).set('minute', 0).format('HH:mm'),
          disabled: false,
        })
        return time
      },
      [] as {
        no: number
        name: string
        disabled: boolean
      }[],
    )

    dispatch(
      setConstants({
        warehouseList: locations.data,
        categories: categoriesMapped,
        dayOptions,
        scheduleOptions,
        scheduleTypeOptions,
        timeOptions,
        skuMaxOptions,
      }),
    )
  } catch (err) {
    callErrorMsg(err)
  } finally {
    dispatch(setIsLoadingConstant(false))
  }
}

export const fetchGetCategoriesV2 = (keyword: string) => async (dispatch: AppDispatchType) => {
  try {
    const categories = await getNewCategoriesAPI({
      'pagination.page_index': 1,
      'pagination.page_size': 100,
      ...(keyword && { 'filter.name': keyword }),
    })

    const categoriesMapped = categories.data.data.map((cat) => ({
      category_id: cat.id,
      category_name: cat.name,
    }))

    dispatch(
      setConstants({
        categories: categoriesMapped,
      }),
    )
  } catch (err) {
    callErrorMsg(err)
  }
}

export const actGetReplenishmentScheduleFilter =
  (params?: ReplenishmentScheduleFilterType) => (dispatch: StoreDispatchType) => {
    const qs = serializeQueryToURL({ page_index: 1, ...params })
    dispatch(setReplenishmentScheduleFilter(qs))
    dispatch(actGetReplenishmentSchedule(qs))
  }

export const fetchGetReplenishmentScheduleDetail =
  (id: number) => async (dispatch: StoreDispatchType, getState: () => RootStateType) => {
    dispatch(setIsLoading(true))
    try {
      const response = await getReplenishmentScheduleDetail(id)
      const {
        replenishmentSchedule: {
          constants: { categories, dayOptions, timeOptions, scheduleTypeOptions, warehouseList },
        },
      } = getState() as StoreStateType
      const { data } = response.data

      const scheduleType =
        scheduleTypeOptions.find((item) => item.value === data.schedule_type.toLocaleLowerCase()) ||
        null
      const categoryIdList = categories.filter((item) =>
        data.category_id.includes(item.category_id),
      )
      const daylist = dayOptions.filter((item) => data.days.includes(item.value))
      const timelist = timeOptions.filter((item) => data.times.includes(item.name))
      const location = warehouseList.find((item) => item.location_id === data.location_id) || null

      const result: FormScheduleDetailType = {
        id: data.id,
        scheduleType,
        categoryIdList,
        daylist,
        timelist,
        location,
        unlimited: data.unlimited,
        maxSku: String(data.max_sku),
        scheduleOption: data.is_now ? 'now' : 'selectSchedule',
        startDate: data.start_date
          ? dateFormat({
              date: new Date(data.start_date),
              format: 'YYYY-MM-DD',
              locale: 'id',
              utc: true,
            })
          : '',
        endDate: data.end_date
          ? dateFormat({
              date: new Date(data.end_date),
              format: 'YYYY-MM-DD',
              locale: 'id',
              utc: true,
            })
          : '',
        isFlagReplenishV3Active: false,
      }

      dispatch(setPopupAddEditFormOnFetchDetailSucceed(result))
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const fetchPostReplenishmentSchedule =
  () => async (dispatch: AppDispatchType, getState: () => RootStateType) => {
    try {
      const {
        replenishmentSchedule: {
          popupAddEditSchedule: {
            form: {
              id,
              daylist,
              timelist,
              categoryIdList,
              scheduleType,
              startDate,
              endDate,
              location,
              maxSku,
              unlimited,
              scheduleOption,
            },
          },
        },
        auth: {
          userData: { email },
        },
      } = getState() as StoreStateType

      const payload: PostReplenishmentScheduleRequestType = {
        dayList: daylist.map((item) => item.value),
        timeList: timelist.map((item) => item.name),
        categoryIdList: categoryIdList.map((item) => item.category_id),
        scheduleType: scheduleType?.value,
        startDate: scheduleOption === 'now' ? '' : startDate,
        endDate,
        createdBy: email,
        locationId: location?.location_id,
        maxSku: +maxSku,
        unlimited: !!unlimited,
        isNow: scheduleOption === 'now',
      }

      const response: AxiosResponse<
        PutReplenishmentScheduleResponseType | PostReplenishmentScheduleResponseType
      > = id
        ? await putReplenishmentScheduleAPI({ id, payload })
        : await postReplenishmentScheduleAPI(payload)

      dispatch(actGetReplenishmentSchedule())
      toastSuccess(response.data.message)
      dispatch(resetPopupAddEditSchedule())
    } catch (err) {
      callErrorMsg(err)
    }
  }

export const fetchPostReplenishmentScheduleV2 =
  () => async (dispatch: AppDispatchType, getState: () => RootStateType) => {
    try {
      const {
        replenishmentSchedule: {
          popupAddEditSchedule: {
            form: {
              id,
              daylist,
              timelist,
              categoryIdList,
              scheduleType,
              startDate,
              endDate,
              location,
              maxSku,
              unlimited,
              scheduleOption,
            },
          },
        },
        auth: {
          userData: { email },
        },
      } = getState() as StoreStateType

      const payload: CreateUpdateReplenishmentSchedulePayloadType = {
        days: daylist.map((item) => item.value),
        times: timelist.map((item) => item.name),
        category_id: categoryIdList.map((item) => item.category_id),
        schedule_type: scheduleType?.value || '',
        start_date: scheduleOption === 'now' ? '' : startDate,
        end_date: endDate,
        created_by: email,
        location_id: location?.location_id as number,
        max_sku: +maxSku,
        unlimited: !!unlimited,
        is_now: scheduleOption === 'now',
      }

      const response = id
        ? await putReplenishmentScheduleWims(id, payload)
        : await postReplenishmentScheduleWims(payload)

      dispatch(actGetReplenishmentSchedule())
      toastSuccess(response.data.message)
      dispatch(resetPopupAddEditSchedule())
    } catch (err) {
      callErrorMsg(err)
    }
  }
export const fetchDeleteReplenishmentSchedule =
  (id: number) => async (dispatch: AppDispatchType) => {
    try {
      const response = await deleteReplenishmentSchedule(id)

      toastSuccess(response.data.message)
      dispatch(actGetReplenishmentSchedule())
    } catch (err) {
      callErrorMsg(err)
    }
  }
